import { postCartUpdates, updateCartCountBadge } from '@trade/utils';
import { createCartUpdateQueue } from '@trade/cart-queue';
import {
  getUnitPriceCentsForRow,
  hydrateRoot,
  normalizeCartItems,
  presentProductFooter,
  presentVariant,
  queryVariantRows,
} from '@trade/row-sync';
import { TRADE_CART_UPDATED, TRADE_QTY_CHANGE } from '@trade/events';

/**
 * @typedef {object} CartLine
 * @property {string} variantId
 * @property {number} quantity
 * @property {number} [pending]
 * @property {string} [lineKey]
 * @property {string} [productId]
 * @property {number} [unitPriceCents]
 */

/**
 * @typedef {object} StoreNotification
 * @property {string[]} changedVariantIds
 * @property {number} [itemCount]
 * @property {number} [totalPrice]
 */

/**
 * @typedef {object} TradeOrderingOptions
 * @property {string} source
 * @property {() => string} [getSectionsUrl]
 * @property {() => string[]} [getSectionIds]
 * @property {number} [debounceMs]
 * @property {number} [maxWaitMs]
 */

export class TradeCartStore {
  /** @type {Map<string, CartLine>} */
  #lines = new Map();

  /** @type {number} */
  #itemCount = 0;

  /** @type {number} */
  #totalPrice = 0;

  /** @type {Set<(detail: StoreNotification) => void>} */
  #listeners = new Set();

  /** @type {Promise<void> | null} */
  #bootstrapPromise = null;

  /**
   * @param {ParentNode} [root]
   */
  bootstrapFromDom(root = document) {
    for (const input of root.querySelectorAll('input[data-cart-quantity]')) {
      if (!(input instanceof HTMLInputElement)) continue;

      const row = input.closest('[data-variant-id]');
      if (!(row instanceof HTMLElement)) continue;

      const variantId = row.dataset.variantId;
      if (!variantId) continue;

      const quantity = parseInt(input.dataset.cartQuantity || input.value || '0', 10) || 0;
      const productCard = row.closest('[data-product-id]');
      const productId =
        productCard instanceof HTMLElement ? productCard.dataset.productId : undefined;
      const unitPriceCents = parseInt(row.dataset.unitPriceCents || '', 10);

      const existing = this.#lines.get(variantId);
      if (existing?.pending != null) continue;

      this.#lines.set(variantId, {
        variantId,
        quantity,
        lineKey: row.dataset.cartLineKey,
        productId: productId || existing?.productId,
        unitPriceCents: !isNaN(unitPriceCents) ? unitPriceCents : existing?.unitPriceCents,
      });
    }
  }

  async bootstrapFromCart() {
    if (this.#bootstrapPromise) return this.#bootstrapPromise;

    this.#bootstrapPromise = (async () => {
      try {
        const response = await fetch(`${Theme.routes.cart_url}.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (!response.ok) return;

        const data = await response.json();
        this.ingestServerCart(data.items, data.item_count, data.total_price, { preservePending: true });
      } catch {
        // Network unavailable — SSR DOM seeds are sufficient for visible rows
      }
    })();

    return this.#bootstrapPromise;
  }

  /**
   * @param {unknown} rawItems
   * @param {number} [itemCount]
   * @param {number} [totalPrice]
   * @param {{ preservePending?: boolean }} [options]
   */
  ingestServerCart(rawItems, itemCount, totalPrice, options = {}) {
    const items = normalizeCartItems(rawItems);
    /** @type {string[]} */
    const changed = [];

    for (const item of items) {
      const variantId = String(item.variant_id);
      const existing = this.#lines.get(variantId);

      if (options.preservePending && existing?.pending != null) {
        this.#lines.set(variantId, {
          ...existing,
          quantity: item.quantity,
          lineKey: item.key ?? existing.lineKey,
          unitPriceCents: item.price ?? existing.unitPriceCents,
          productId: item.product_id != null ? String(item.product_id) : existing.productId,
        });
        changed.push(variantId);
        continue;
      }

      this.#lines.set(variantId, {
        variantId,
        quantity: item.quantity,
        lineKey: item.key,
        unitPriceCents: item.price,
        productId: item.product_id != null ? String(item.product_id) : existing?.productId,
      });
      changed.push(variantId);
    }

    if (itemCount != null) this.#itemCount = itemCount;
    if (totalPrice != null) this.#totalPrice = totalPrice;

    if (changed.length > 0) {
      this.#notify(changed, { itemCount: this.#itemCount, totalPrice: this.#totalPrice });
    } else if (itemCount != null) {
      updateCartCountBadge(this.getDisplayItemCount());
    }
  }

  /**
   * @param {string} variantId
   * @param {number} quantity
   * @param {{ productId?: string }} [options]
   */
  setPending(variantId, quantity, options = {}) {
    const key = String(variantId);
    const existing = this.#lines.get(key);
    const committed = existing?.quantity ?? 0;

    const line = {
      variantId: key,
      quantity: committed,
      pending: quantity,
      lineKey: existing?.lineKey,
      productId: options.productId || existing?.productId,
      unitPriceCents: existing?.unitPriceCents,
    };

    if (!line.unitPriceCents) {
      for (const row of queryVariantRows(document, key)) {
        const cents = getUnitPriceCentsForRow(row);
        if (cents > 0) {
          line.unitPriceCents = cents;
          break;
        }
      }
    }

    this.#lines.set(key, line);
    this.#notify([key]);
    updateCartCountBadge(this.getDisplayItemCount());
  }

  /**
   * @param {string[]} variantIds
   */
  revert(variantIds) {
    for (const variantId of variantIds) {
      const key = String(variantId);
      const line = this.#lines.get(key);
      if (!line) continue;

      this.#lines.set(key, {
        variantId: key,
        quantity: line.quantity,
        lineKey: line.lineKey,
        productId: line.productId,
        unitPriceCents: line.unitPriceCents,
      });
    }

    this.#notify(variantIds);
    updateCartCountBadge(this.getDisplayItemCount());
  }

  /**
   * @param {string} variantId
   * @returns {CartLine | undefined}
   */
  get(variantId) {
    return this.#lines.get(String(variantId));
  }

  /**
   * @param {string} variantId
   * @returns {number}
   */
  getCommittedQty(variantId) {
    return this.#lines.get(String(variantId))?.quantity ?? 0;
  }

  /**
   * @param {string} productId
   * @returns {CartLine[]}
   */
  getProductLines(productId) {
    return Array.from(this.#lines.values()).filter(
      (line) => line.productId === String(productId) && (line.pending ?? line.quantity) > 0
    );
  }

  getItemCount() {
    return this.#itemCount;
  }

  getTotalPrice() {
    return this.#totalPrice;
  }

  getDisplayItemCount() {
    let count = this.#itemCount;

    for (const line of this.#lines.values()) {
      if (line.pending == null) continue;
      count += line.pending - line.quantity;
    }

    return Math.max(count, 0);
  }

  /**
   * Cart subtotal including uncommitted pending qty deltas for known lines.
   */
  getDisplayTotalPrice() {
    let total = this.#totalPrice;

    for (const line of this.#lines.values()) {
      if (line.pending == null || line.unitPriceCents == null) continue;
      total += (line.pending - line.quantity) * line.unitPriceCents;
    }

    return Math.max(0, total);
  }

  /**
   * Apply a flush response, skipping variants whose pending qty moved since send.
   *
   * @param {Record<string, number>} sentUpdates
   * @param {unknown} rawItems
   * @param {{ itemCount?: number; totalPrice?: number }} [meta]
   * @returns {string[]} variant IDs that need another flush
   */
  commitFromFlush(sentUpdates, rawItems, meta = {}) {
    const items = normalizeCartItems(rawItems);
    /** @type {string[]} */
    const committedIds = [];
    /** @type {string[]} */
    const needsReflush = [];

    for (const [variantId, sentQty] of Object.entries(sentUpdates)) {
      const key = String(variantId);
      const existing = this.#lines.get(key);

      if (existing?.pending != null && existing.pending !== sentQty) {
        needsReflush.push(key);
        continue;
      }

      const serverItem = items.find((item) => String(item.variant_id) === key);

      if (serverItem) {
        this.#lines.set(key, {
          variantId: key,
          quantity: serverItem.quantity,
          lineKey: serverItem.key ?? existing?.lineKey,
          productId: serverItem.product_id != null ? String(serverItem.product_id) : existing?.productId,
          unitPriceCents: serverItem.price ?? existing?.unitPriceCents,
        });
      } else {
        this.#lines.set(key, {
          variantId: key,
          quantity: 0,
          productId: existing?.productId,
          unitPriceCents: existing?.unitPriceCents,
        });
      }

      committedIds.push(key);
    }

    if (meta.itemCount != null) this.#itemCount = meta.itemCount;
    if (meta.totalPrice != null) this.#totalPrice = meta.totalPrice;

    if (committedIds.length > 0) {
      this.#notify(committedIds, meta);
    }

    updateCartCountBadge(this.getDisplayItemCount());

    return needsReflush;
  }

  /**
   * @param {(detail: StoreNotification) => void} listener
   */
  subscribe(listener) {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  /**
   * @param {string[]} changedVariantIds
   * @param {{ itemCount?: number; totalPrice?: number }} [extra]
   */
  #notify(changedVariantIds, extra = {}) {
    const detail = { changedVariantIds, ...extra };

    for (const listener of this.#listeners) {
      listener(detail);
    }
  }
}

/** @type {TradeCartStore | null} */
let storeInstance = null;

export function getTradeCartStore() {
  if (!storeInstance) {
    storeInstance = new TradeCartStore();
  }
  return storeInstance;
}

/** @type {import('@trade/cart-queue').TradeCartUpdateQueue | null} */
let queueInstance = null;

export function getTradeCartQueue() {
  if (!queueInstance) {
    throw new Error('Trade cart queue not initialized — call initTradeOrdering first');
  }
  return queueInstance;
}

/**
 * Flush pending cart updates immediately (e.g. blur, checkout, page hide).
 * @returns {Promise<void>}
 */
export function flushTradeCartNow() {
  return queueInstance?.flushNow() ?? Promise.resolve();
}

/** @type {boolean} */
let leaveFlushListenersAttached = false;

/** @type {Set<HTMLElement>} */
const orderingRoots = new Set();

/** @type {boolean} */
let documentQtyListenerAttached = false;

/**
 * @param {string[]} changedVariantIds
 */
function presentStoreChanges(changedVariantIds) {
  const store = getTradeCartStore();
  /** @type {Set<string>} */
  const productIds = new Set();

  for (const variantId of changedVariantIds) {
    presentVariant(variantId, store.get(variantId));
    const line = store.get(variantId);
    if (line?.productId) productIds.add(line.productId);
  }

  for (const productId of productIds) {
    presentProductFooter(productId, store.getProductLines(productId));
  }
}

/**
 * @param {HTMLElement} root
 * @param {string} variantId
 */
function setVariantInputsToZero(root, variantId) {
  for (const row of queryVariantRows(root, variantId)) {
    for (const input of row.querySelectorAll('input[data-cart-quantity]')) {
      if (input instanceof HTMLInputElement) input.value = '0';
    }
  }
}

/**
 * @param {HTMLElement} root
 * @param {TradeOrderingOptions} options
 */
export function initTradeOrdering(root, options) {
  const store = getTradeCartStore();

  const debounceMs = options.debounceMs ?? (options.source === 'trade-collection' ? 600 : 400);
  const maxWaitMs = options.maxWaitMs ?? (options.source === 'trade-collection' ? 2000 : 1500);

  if (!queueInstance) {
    queueInstance = createCartUpdateQueue({
      component: root,
      store,
      source: options.source,
      getSectionsUrl: options.getSectionsUrl ?? (() => window.location.pathname),
      getSectionIds: options.getSectionIds ?? (() => []),
      debounceMs,
      maxWaitMs,
      onError: (message, variantIds) => {
        root.dispatchEvent(
          new CustomEvent('trade:cart-error', {
            bubbles: true,
            detail: { message, variantIds },
          })
        );
      },
    });

    store.subscribe(({ changedVariantIds }) => {
      presentStoreChanges(changedVariantIds);
    });

    document.addEventListener(TRADE_CART_UPDATED, onExternalCartUpdated);
    store.bootstrapFromCart();
    attachLeaveFlushListeners();
  }

  if (!orderingRoots.has(root)) {
    orderingRoots.add(root);
    root.addEventListener('click', onOrderingClick);
    root.addEventListener('trade:cart-error', onCartError);
  }

  ensureDocumentQtyListener();

  store.bootstrapFromDom(root);
  hydrateRoot(root, store);

  return { store, queue: queueInstance };
}

function attachLeaveFlushListeners() {
  if (leaveFlushListenersAttached) return;
  leaveFlushListenersAttached = true;

  window.addEventListener('pagehide', () => {
    queueInstance?.flushNow();
  });

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const checkout =
        target.closest('a[href*="/checkout"]') ||
        target.closest('.trade-cart-toolbar__checkout') ||
        target.closest('button[name="checkout"]');

      if (checkout) {
        queueInstance?.flushNow();
      }
    },
    { capture: true }
  );
}

function ensureDocumentQtyListener() {
  if (documentQtyListenerAttached) return;
  documentQtyListenerAttached = true;
  document.addEventListener(TRADE_QTY_CHANGE, onDocumentQtyChange);
}

/**
 * @param {HTMLElement} root
 * @param {CustomEvent} event
 */
function processQtyChange(root, event) {
  const target = event.target instanceof Node ? event.target : root;
  if (!root.contains(target)) return;

  const { variantId, quantity } = event.detail ?? {};
  if (!variantId || quantity == null) return;

  const productCard = target instanceof Element ? target.closest('[data-product-id]') : null;
  const productId = productCard instanceof HTMLElement ? productCard.dataset.productId : undefined;

  if (!queueInstance) {
    console.error('[trade-cart] Queue not initialized — call initTradeOrdering first');
    return;
  }

  queueInstance.enqueueUpdate(variantId, quantity, { productId });
}

/** @param {Event} event */
function onDocumentQtyChange(event) {
  if (!(event instanceof CustomEvent)) return;

  const target = event.target;
  if (!(target instanceof Node)) return;

  for (const root of orderingRoots) {
    if (root.contains(target)) {
      processQtyChange(root, event);
      return;
    }
  }

  const host =
    target instanceof Element
      ? target.closest('trade-collection') ||
        target.closest('trade-cart') ||
        target.closest('.trade-product-order-section') ||
        target.closest('[data-trade-ordering-root]')
      : null;

  if (!(host instanceof HTMLElement)) return;

  initTradeOrdering(host, {
    source: host.matches('trade-cart') ? 'trade-cart' : 'trade-collection',
  });
  processQtyChange(host, event);
}

/** @param {Event} event */
function onOrderingClick(event) {
  const root = event.currentTarget;
  if (!(root instanceof HTMLElement)) return;

  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const removeButton = target.closest('[data-trade-remove-variant]');
  if (removeButton instanceof HTMLElement && root.contains(removeButton)) {
    event.preventDefault();
    const variantId = removeButton.dataset.tradeRemoveVariant;
    if (!variantId) return;

    setVariantInputsToZero(root, variantId);
    queueInstance?.enqueueUpdate(variantId, 0);
    return;
  }

  const removeAllButton = target.closest('[data-trade-remove-all]');
  if (!(removeAllButton instanceof HTMLElement) || !root.contains(removeAllButton)) return;

  event.preventDefault();

  const productCard =
    removeAllButton.closest('[data-product-id]') || root.querySelector('[data-product-id]');
  const productId = productCard instanceof HTMLElement ? productCard.dataset.productId : undefined;

  /** @type {Record<string, number>} */
  const updates = {};
  /** @type {Set<string>} */
  const seen = new Set();

  if (productId && queueInstance) {
    const store = getTradeCartStore();
    for (const line of store.getProductLines(productId)) {
      if (!line.variantId || seen.has(line.variantId)) continue;
      seen.add(line.variantId);
      updates[line.variantId] = 0;
    }
  }

  // Also clear visible rows (covers pending DOM qty not yet reflected in store lines).
  for (const row of queryVariantRows(root)) {
    const variantId = row.dataset.variantId;
    if (!variantId || seen.has(variantId)) continue;

    const input = row.querySelector('input[data-cart-quantity]');
    if (!(input instanceof HTMLInputElement)) continue;

    const inputQty = parseInt(input.value || '0', 10) || 0;
    const committedQty = parseInt(input.dataset.cartQuantity || '0', 10) || 0;
    if (inputQty > 0 || committedQty > 0) {
      seen.add(variantId);
      updates[variantId] = 0;
    }
  }

  if (Object.keys(updates).length === 0) return;

  for (const variantId of Object.keys(updates)) {
    setVariantInputsToZero(root, variantId);
  }

  queueInstance?.enqueueBatch(updates);
}

/** @param {Event} event */
function onCartError(event) {
  if (!(event instanceof CustomEvent)) return;

  const root = event.currentTarget;
  if (!(root instanceof HTMLElement)) return;

  const message = event.detail?.message;
  if (typeof message !== 'string') return;

  const errorContainer = root.querySelector('[data-trade-error]');
  const errorText = root.querySelector('[data-trade-error-text]');
  if (errorText instanceof HTMLElement) errorText.textContent = message;
  if (errorContainer instanceof HTMLElement) errorContainer.classList.remove('hidden');
}

/** @param {Event} event */
function onExternalCartUpdated(event) {
  if (!(event instanceof CustomEvent)) return;
  if (event.detail?.origin === queueInstance?.component) return;

  const items = event.detail?.items;
  if (items) {
    getTradeCartStore().ingestServerCart(items, event.detail?.itemCount, event.detail?.totalPrice);
    return;
  }

  const { changedVariantIds } = event.detail ?? {};
  if (!Array.isArray(changedVariantIds) || changedVariantIds.length === 0) return;

  presentStoreChanges(changedVariantIds);
}

/**
 * @param {boolean} includeCartSection
 * @param {string | undefined} cartSectionId
 * @returns {string[]}
 */
export function getCartSectionIds(includeCartSection, cartSectionId) {
  /** @type {string[]} */
  const sectionIds = [];

  if (includeCartSection && cartSectionId) {
    sectionIds.push(cartSectionId);
  }

  const cartRoot = document.querySelector('trade-cart[data-section-id]');
  if (cartRoot instanceof HTMLElement) {
    const id = cartRoot.dataset.sectionId;
    if (id && !sectionIds.includes(id)) sectionIds.push(id);
  }

  return sectionIds;
}

export { postCartUpdates, updateCartCountBadge } from '@trade/utils';

ensureDocumentQtyListener();

if (typeof window !== 'undefined') {
  window.__tradeCartDebug = () => ({
    hasCollectionCE: !!customElements.get('trade-collection'),
    orderingRoots: orderingRoots.size,
    queueReady: !!queueInstance,
    storeLines: getTradeCartStore().getItemCount(),
  });
}
